import {
  forwardRef,
  Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';

import classNames from 'classnames';
import { FieldError } from 'react-hook-form';
import styled from 'styled-components';

import { usePrevious } from 'utils/reactHooks';

import { FormHelperText } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

import { Icon } from '@sdb/planning-ui';

const SelectStyles = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;
`;

export interface CustomSelectRef {
  focus: () => void;
}

export type CustomSelectProps<T> = {
  autofocus?: boolean;
  label?: string;
  className?: string;
  options: {
    value: T;
    label: string;
    style?: Record<string, any> | undefined;
    disabled?: boolean;
  }[];
  disabled?: boolean;
  onSelected: (option) => void;
  hasClearButton?: boolean;
  valueProp?: T;
  fullWidth?: boolean;
  error?: FieldError;
  required?: boolean;
  variant?: 'standard' | 'outlined' | 'filled';
  onBlur?: () => void;
  placeholder?: string;
  displayEmpty?: boolean;
  autoSelectSingleOption?: boolean;
  openOnFocus?: boolean;
  openOnEnter?: boolean;
};

const CustomSelect = (
  props: CustomSelectProps<string | number>,
  ref: Ref<CustomSelectRef> | undefined
) => {
  const {
    label,
    className,
    autofocus = false,
    options = [],
    disabled = false,
    onSelected,
    hasClearButton,
    valueProp,
    fullWidth,
    error,
    required,
    variant = 'standard',
    placeholder,
    displayEmpty = false,
    onBlur,
    autoSelectSingleOption = true,
    openOnFocus = false
  } = props;

  const [value, setValue] = useState<string | number>(valueProp ?? '');
  const [_options, setOptions] = useState(options);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Expose focus method to parent component
  useImperativeHandle(ref, () => ({
    focus: () => {
      // Focus the underlying input element
      inputRef.current?.focus();
    }
  }));

  const prevValue = usePrevious(value);
  useEffect(() => {
    if (prevValue !== undefined && prevValue !== value) {
      onSelected(value);
    }
  }, [value, prevValue, onSelected]);

  const prevValueProp = usePrevious(valueProp);
  useEffect(() => {
    if (
      (typeof valueProp === 'string' || typeof valueProp === 'number') &&
      valueProp !== prevValueProp
    ) {
      setValue(valueProp);
    }
  }, [valueProp, prevValueProp]);

  useEffect(() => {
    setOptions(options);

    if (autoSelectSingleOption && options.length === 1) {
      setValue(options[0].value);
    }
  }, [options, autoSelectSingleOption]);

  const handleFocus = () => {
    if (openOnFocus && !disabled) {
      setOpen(true);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;

    if (event.key === 'Escape' && open) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      return;
    }
  };

  return (
    <SelectStyles className={classNames(className)}>
      <FormControl
        size="small"
        variant={variant}
        fullWidth
        error={!!error?.type}
      >
        {!placeholder && label && (
          <InputLabel htmlFor={label}>
            {label}
            {required && ' *'}
          </InputLabel>
        )}
        <Select
          fullWidth={fullWidth}
          autoFocus={autofocus}
          label={label}
          value={value}
          variant={variant}
          size="small"
          onChange={(e) => setValue(e.target.value)}
          disabled={!_options.length || disabled}
          inputRef={inputRef}
          error={!!error?.type}
          onBlur={onBlur}
          displayEmpty={displayEmpty}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          inputProps={{
            required
          }}
        >
          {placeholder && (
            <MenuItem value="" disabled>
              {placeholder}
            </MenuItem>
          )}
          {_options.map(({ value, label, style, disabled: disabledOption }) => (
            <MenuItem
              key={value}
              value={value}
              style={style}
              disabled={disabledOption}
            >
              {label}
            </MenuItem>
          ))}
        </Select>
        {error && (
          <FormHelperText error={!!error?.type}>
            {error?.message}
          </FormHelperText>
        )}
      </FormControl>
      {hasClearButton && (
        <IconButton
          color="primary"
          onClick={() => setValue('')}
          size="medium"
          disabled={!_options.length || !value}
        >
          <Icon type="xmark" fontSize="small" />
        </IconButton>
      )}
    </SelectStyles>
  );
};

export default forwardRef(CustomSelect);
